/**
 * Centralised Axios instance.
 * All API calls should use this client — it automatically:
 *  1. Attaches the Authorization: Bearer <token> header
 *  2. On 401, clears the stored token and broadcasts a "session-expired" event
 *     so the UserContext can show a login prompt.
 *
 * To switch to a different base URL, update REACT_APP_API_BASE_URL in .env.
 */

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:4000';

export const TOKEN_KEY = 'esg-auth-token';
export const REFRESH_TOKEN_KEY = 'esg-auth-refresh-token';

export const httpClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ─── Request Interceptor ─────────────────────────────────────────────────────
// Attach JWT from localStorage before every request
httpClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && config.headers) {
    (config.headers as Record<string, string>).Authorization =
      `Bearer ${token}`;
  }

  return config;
});

// ─── Response Interceptor ────────────────────────────────────────────────────
let onAuthFailed: (() => void) | null = null;
export const setAuthFailedCallback = (cb: () => void): void => {
  onAuthFailed = cb;
};

// On 401, clear token and execute callback so User Store can react
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      // Custom callback picked up by UserStore to show login screen
      if (onAuthFailed) onAuthFailed();
    }

    return await Promise.reject(error);
  }
);

export default httpClient;
